import { makeStyles } from "@material-ui/core/styles";
import { navigate } from "@reach/router";
import { Wrapper } from "../base/Wrapper";
import TitleBar from "../TitleBar";
import Typography from "@material-ui/core/Typography";
import { Avatar } from "@material-ui/core";
import Snackbar from "../base/Snackbar";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Divider from "@material-ui/core/Divider";
import { MdCollectionsBookmark, MdStar, MdMail } from "react-icons/md";
import { GoLaw } from "react-icons/go";
import { CgProfile } from "react-icons/cg";
import { GiShare } from "react-icons/gi";
import Button from "@material-ui/core/Button";
import CardActionArea from "@material-ui/core/CardActionArea";
import { signOut } from "../../auth/signOut";
import { getCurrentUserInfo } from "../../auth/getCurrentUserInfo";
import { useEffect, useRef, useState } from "react";
import Skeleton from "@material-ui/lab/Skeleton";
import PointsBar from "./PointsBar";
import { HiOutlineColorSwatch } from "react-icons/hi";
import { AiOutlineAreaChart } from "react-icons/ai";
import { getFollowerData } from "../../auth/getFollowerData";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  profileDetails: {
    display: "flex",
    flexDirection: "row",
    marginBottom: theme.spacing(2.5),
  },
  avatar: {
    width: theme.spacing(12.5),
    height: theme.spacing(12.5),
    marginRight: theme.spacing(2.5),
  },
  clickArea: {
    display: "flex",
    padding: theme.spacing(1),
  },
  avatarbg: {
    backgroundColor: theme.palette.primary.main,
  },
  name: {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(0.5),
  },
  settingsList: {
    marginTop: theme.spacing(2.5),
  },
  logout: {
    paddingBottom: theme.spacing(5),
  },
  logoutBtn: {
    margin: theme.spacing(4),
  },
}));

export default function Profile() {
  const classes = useStyles();

  const [followerData, setFollowerData] = useState({ follower: [], following: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [userName, setUserName] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [sumTime, setSumTime] = useState("");
  const [snackbarContent, setSnackbarContent] = useState();

  useEffect(() => {
    const loadUserInfo = async () => {
      const userInfo = await getCurrentUserInfo();

      if (userInfo) {
        const followerData = await getFollowerData(userInfo.id);
        setUserName(userInfo.userName ?? "");
        setPhotoUrl(userInfo.photoUrl ?? "");
        setSumTime(userInfo.sumTime ?? 0);
        setFollowerData(followerData);

        setIsLoading(false);
      }
    };

    loadUserInfo();
  }, []);

  const onClickSignOut = async () => {
    try {
      await signOut();
      navigate(`/`);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: `Fitness Trail Mosbach`,
        text: `Siehe dir die Fitness-Trail App von Mosbach an`,
        url: "https://fitness-trail-mosbach.de",
      });
    } else {
      // When share is not supported
      copyToClippboard();
      setSnackbarContent({
        message: "Ein Link wurde in deine Zwischenablage kopiert!",
        status: "success",
        open: true,
      });
    }
  };

  const inputRef = useRef();

  function copyToClippboard() {
    inputRef.current.style.display = "block";
    inputRef.current.select();
    document.execCommand("copy");
    inputRef.current.style.display = "none";
  }

  const LinkItem = ({ Icon, title, subtitle, link, share, state }) => {
    return (
      <>
        <CardActionArea
          className={classes.clickArea}
          onClick={
            share
              ? handleShare
              : () => {
                  navigate(link);
                }
          }
        >
          <ListItemAvatar>
            <Avatar className={classes.avatarbg}>
              <Icon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary={title} secondary={subtitle} />
        </CardActionArea>
        {share && <input value="https://fitness-trail-mosbach.de" ref={inputRef} readOnly hidden />}
        <Divider />
      </>
    );
  };

  return (
    <div>
      <TitleBar title="Profil" />
      <Wrapper>
        <div className={classes.root}>
          <Snackbar snackbarContent={snackbarContent} setSnackbarContent={setSnackbarContent} />
          <div style={{ width: "100%" }}>
            <div className={classes.profileDetails}>
              {isLoading ? (
                <Skeleton variant="circle" className={classes.avatar} />
              ) : (
                <Avatar
                  className={classes.avatar}
                  alt={userName}
                  src={photoUrl}
                  referrerPolicy="no-referrer"
                />
              )}
              <div>
                {isLoading ? (
                  <Skeleton width="60%" height="20%" style={{ margin: "20px 0 0 0" }} />
                ) : (
                  <Typography className={classes.name} variant="h5">
                    {userName}
                  </Typography>
                )}
                <Typography
                  className={classes.name}
                  variant="caption"
                  color="primary"
                  onClick={() => navigate("/follow")}
                >
                  {followerData.follower.length} Follower | {followerData.following.length} Folge
                  ich
                </Typography>
              </div>
            </div>
            <PointsBar sumTime={sumTime} loading={isLoading} navigateErfolge />
            <div className={classes.settingsList}>
              <Divider />
              <LinkItem
                Icon={CgProfile}
                title="Profil bearbeiten"
                subtitle="Ändere deinen Benutzername oder Profilbild"
                link="./settings"
              />
              <LinkItem
                Icon={MdStar}
                title="Erfolge"
                subtitle="Sieh dir deine Abzeichen an"
                link="./erfolge"
              />
              <LinkItem
                Icon={AiOutlineAreaChart}
                title="Statistiken"
                subtitle="Detaillierte Statistiken zu deinem Training"
                link="./statistik"
              />
              <LinkItem
                Icon={HiOutlineColorSwatch}
                title="Aussehen & Barrierefreiheit"
                subtitle="Ändere das Erscheinungsbild der App"
                link="./accessibility"
              />
              <LinkItem
                Icon={GiShare}
                title="Mit anderen teilen"
                subtitle="Teile diese App mit Freunden und Familie"
                share
              />
              <LinkItem
                Icon={MdCollectionsBookmark}
                title="Privatsphäre & Datenschutz"
                subtitle="Die Sicherheit deiner Daten ist uns wichtig"
                link="./privacy"
              />
              <LinkItem
                Icon={GoLaw}
                title="Impressum"
                subtitle="Impressum im Überlick"
                link="./impressum"
              />
              <LinkItem
                Icon={MdMail}
                title="Kontakt"
                subtitle="Kontaktiere uns bei Fragen und Anregungen zur App"
                link="mailto:webdesign.dhbw@gmail.com?subject=Trail-App-Mosbach"
              />
            </div>
          </div>
          <div className={classes.logout}>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={onClickSignOut}
              className={classes.logoutBtn}
            >
              Ausloggen
            </Button>
          </div>
        </div>
      </Wrapper>
    </div>
  );
}
